import React, { useEffect, useContext, useRef, useState, useMemo, useCallback } from 'react';
import { useActor } from '@xstate/react';
import Fuse from 'fuse.js';
import { GlobalStateContext } from '../state/app/index';
import { useHotkeys } from 'react-hotkeys-hook';
import SearchIcon from '@mui/icons-material/Search';
import logger from '../logger';

const MAX_RESULTS = 8;

const NoteSearchPalette = () => {
  const globalServices = useContext(GlobalStateContext);
  const [appState, sendAppState] = useActor(globalServices.appState);
  const {
    context: { showNoteSearch, notesList, notes },
  } = appState;

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const allNotes = useMemo(() => {
    const raw = notesList?.length ? notesList : notes ? Object.values(notes) : [];
    return raw.filter((n) => n && !n.trash);
  }, [notesList, notes]);

  const fuse = useMemo(
    () =>
      new Fuse(allNotes, {
        keys: [
          { name: 'note_title', weight: 3 },
          { name: 'note_snippet', weight: 1 },
        ],
        includeScore: true,
        threshold: 0.4,
        minMatchCharLength: 1,
      }),
    [allNotes]
  );

  const results = useMemo(() => {
    if (!query.trim()) {
      // Show most-recently-modified notes when no query
      return [...allNotes]
        .sort((a, b) => new Date(b.date_modified) - new Date(a.date_modified))
        .slice(0, MAX_RESULTS)
        .map((item) => ({ item, score: 0 }));
    }
    return fuse.search(query).slice(0, MAX_RESULTS);
  }, [query, fuse, allNotes]);

  const close = useCallback(() => {
    sendAppState({ type: 'closeNoteSearch' });
  }, [sendAppState]);

  const selectResult = useCallback(
    (note) => {
      if (!note?.note_id) return;
      logger.info({ noteId: note.note_id }, 'NoteSearchPalette: selecting note');
      sendAppState({ type: 'selectNote', data: { noteId: note.note_id } });
      close();
    },
    [sendAppState, close]
  );

  // Reset state when opened
  useEffect(() => {
    if (showNoteSearch) {
      setQuery('');
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [showNoteSearch]);

  // Keep active index in range when results change
  useEffect(() => {
    setActiveIndex(0);
  }, [results.length]);

  // Scroll active item into view
  useEffect(() => {
    const item = listRef.current?.children[activeIndex];
    item?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  useHotkeys('esc', close, { enabled: showNoteSearch, enableOnFormTags: true });

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[activeIndex]) selectResult(results[activeIndex].item);
    }
  };

  if (!showNoteSearch) return null;

  return (
    <div className="palette-wrapper" onClick={close}>
      <div className="palette note-search-palette" onClick={(e) => e.stopPropagation()}>
        <div className="palette__accent">
          <div className="palette__accent-icon">
            <SearchIcon sx={{ color: '#fff', fontSize: 26 }} />
          </div>
        </div>
        <div className="palette__body">
          <div className="palette__header">
            <h3 className="palette__title">Search Notes</h3>
          </div>
          <div className="palette__content note-search-palette__content">
            <input
              ref={inputRef}
              className="note-search-palette__input"
              type="text"
              placeholder="Search by title or content…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              autoComplete="off"
              spellCheck={false}
            />
            {results.length > 0 && (
              <ul ref={listRef} className="note-search-palette__results">
                {results.map(({ item: note }, index) => (
                  <li
                    key={note.note_id}
                    className={`note-search-palette__result${index === activeIndex ? ' note-search-palette__result--active' : ''}`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => selectResult(note)}
                  >
                    <span className="note-search-palette__result-title">
                      {note.note_title || 'Untitled'}
                    </span>
                    {note.note_snippet && (
                      <span className="note-search-palette__result-snippet">
                        {note.note_snippet}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {query.trim() && results.length === 0 && (
              <p className="note-search-palette__empty">No notes found</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoteSearchPalette;
